/**
 * The iPad never calls mutation endpoints directly: every action goes into an ordered local
 * queue (IndexedDB) and is sent to POST /api/sync — immediately when online, later when not.
 * Each item has a client-generated id, so replays are harmless (the server keeps a receipt).
 */
import { z } from 'zod';
import { CustomerNotPresentInput, FieldReportInput, LubeSaveInput, PartLineInput, RentalSaveInput, SignatureForm, SignatureRole } from './forms.js';
/** The Work Performed note sent with a clock-out (required for job time — docs/02 §7). */
const workNote = z.string().max(10_000).optional();
export const CLIENT_ID = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
const woId = z.number().int().positive();
const base = {
    clientId: CLIENT_ID,
    userId: z.number().int().positive(),
    /** Device clock when the mechanic did it. */
    deviceTime: z.string().datetime(),
    /** True if it was queued while the device had no connection. */
    offline: z.boolean(),
};
export const SyncItem = z.discriminatedUnion('type', [
    // `note`: what was done on the job being left (switching jobs ends that punch).
    z.object({ ...base, type: z.literal('clock_in'), workOrderId: woId, note: workNote }),
    // No workOrderId: ends whatever is open (job or non-billable time).
    z.object({ ...base, type: z.literal('clock_out'), workOrderId: woId.optional(), note: workNote, reason: z.enum(['LUNCH', 'DAY', 'OUT']).optional() }),
    // Non-billable time not on a work order (settings.timeClock categories). `note` as for clock_in.
    z.object({ ...base, type: z.literal('time_in'), category: z.string().min(1).max(40), note: workNote }),
    z.object({
        ...base,
        type: z.literal('note'),
        workOrderId: woId,
        kind: z.enum(['WORK_PERFORMED', 'ADDITIONAL_ISSUE', 'CORRECTION']),
        body: z.string().max(10_000),
        correctsNoteId: z.number().int().positive().optional(),
    }),
    z.object({ ...base, type: z.literal('submit_draft'), audioClientId: CLIENT_ID, body: z.string().max(10_000) }),
    z.object({ ...base, type: z.literal('discard_draft'), audioClientId: CLIENT_ID }),
    z.object({ ...base, type: z.literal('hour_meter'), workOrderId: woId, reading: z.number().min(0).max(1_000_000), photoClientId: CLIENT_ID }),
    // `workNote`: required when the mechanic is clocked in on the job (hold clocks them out).
    z.object({ ...base, type: z.literal('hold'), workOrderId: woId, holdReason: z.string().max(60), reason: z.string().max(5000).optional(), workNote }),
    z.object({ ...base, type: z.literal('resume'), workOrderId: woId }),
    z.object({ ...base, type: z.literal('ready'), workOrderId: woId }),
    // ---- Phase 3 forms (docs/04). Prices are never part of a mechanic item. ----
    z.object({ ...base, type: z.literal('part_add'), workOrderId: woId, ...PartLineInput.shape }),
    z.object({
        ...base,
        type: z.literal('part_remove'),
        workOrderId: woId,
        /** The line's server id, or the clientId of the part_add that created it (offline). */
        lineId: z.number().int().positive().optional(),
        lineClientId: CLIENT_ID.optional(),
        reason: z.string().trim().min(3).max(2000),
    }),
    z.object({ ...base, type: z.literal('no_parts_used'), workOrderId: woId, value: z.boolean() }),
    z.object({ ...base, type: z.literal('field_report'), workOrderId: woId, ...FieldReportInput.shape }),
    z.object({ ...base, type: z.literal('lube_save'), workOrderId: woId, ...LubeSaveInput.shape }),
    z.object({ ...base, type: z.literal('lube_submit'), workOrderId: woId }),
    z.object({ ...base, type: z.literal('rental_save'), workOrderId: woId, ...RentalSaveInput.shape }),
    z.object({ ...base, type: z.literal('rental_photo'), workOrderId: woId, slot: z.string().trim().min(1).max(80), photoClientId: CLIENT_ID }),
    z.object({ ...base, type: z.literal('rental_submit'), workOrderId: woId }),
    z.object({
        ...base,
        type: z.literal('signature'),
        workOrderId: woId,
        form: SignatureForm,
        role: SignatureRole,
        signerName: z.string().trim().max(120).optional(),
        fileClientId: CLIENT_ID,
    }),
    /** The technician records "Customer not present" (reason, at least 10 characters) or clears it (null). */
    z.object({ ...base, type: z.literal('customer_not_present'), workOrderId: woId, ...CustomerNotPresentInput.shape }),
]);
export const SyncRequest = z.object({
    /** Device clock at the moment of sending — lets the server measure clock skew. */
    sentAt: z.string().datetime(),
    items: z.array(SyncItem).min(1).max(200),
});
/** Headers for PUT /api/sync/files/:clientId (the body is the raw file). */
export const FILE_HEADERS = {
    userId: 'x-wo-user',
    workOrderId: 'x-wo-work-order',
    kind: 'x-wo-kind',
    purpose: 'x-wo-purpose',
    noteKind: 'x-wo-note-kind',
    noteClientId: 'x-wo-note-client-id',
    deviceTime: 'x-wo-device-time',
    sentAt: 'x-wo-sent-at',
    offline: 'x-wo-offline',
};
export const FILE_PURPOSES = ['general', 'hour_meter', 'issue', 'note', 'rental', 'signature'];
//# sourceMappingURL=sync.js.map